// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-8 — "Waiting for your signature" on the provider portal: the documents a physician signs inside OwlRx
// (today the Plan of Treatment). Server-paged (GET carerx/esign/inapp/my-pending, canonical envelope); signing
// opens the shared InAppSignDialog — read the exact PDF, re-enter password + authenticator code, sign. Nothing is
// emailed (D-CRX-87 §4a); this card is how the physician finds it. Hidden when nothing is waiting.
import { useState } from 'react'
import Box from '@mui/material/Box'
import { formatUsDateTime } from '../../api/format'
import { Pager } from '../Pager'
import AppButton from '../ui/AppButton'
import Section from '../ui/Section'
import StatusChip from '../ui/StatusChip'
import { useMyPendingSignatures } from '../../hooks/queries/inappEsign'
import InAppSignDialog from './InAppSignDialog'

const PAGE_SIZE = 10
const TH = { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--color-text-soft)', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' } as const
const TD = { padding: '8px', borderBottom: '1px solid var(--color-border)', fontSize: 13, display: 'flex', alignItems: 'center', minWidth: 0 } as const

export default function PendingSignaturesCard() {
  const [page, setPage] = useState(1)
  const [signing, setSigning] = useState<string | null>(null)
  const q = useMyPendingSignatures({ page, pageSize: PAGE_SIZE })
  const items = q.data?.items ?? []
  if (!q.data || q.data.total === 0) return null
  return (
    <Section title={`Waiting for your signature (${q.data.total})`}
      subtitle="Documents your pharmacy sent you to sign in OwlRx. Open one to read it, then sign with your password and authenticator code.">
      <Box role="table" aria-label="Waiting for your signature" sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 560, display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 1.6fr 1.2fr 1fr' }}>
          {['Document', 'Patient', 'Drug', 'Sent', ''].map(h => <Box key={h} role="columnheader" sx={TH}>{h}</Box>)}
          {items.map(r => (
            <Box key={r.signer_id} role="row" sx={{ display: 'contents' }}>
              <Box role="cell" sx={TD}><StatusChip status="pending" label={r.subject_label} tone="amber" /></Box>
              <Box role="cell" sx={{ ...TD, fontWeight: 700 }}>{r.patient_name ?? '—'}</Box>
              <Box role="cell" sx={TD}>{r.drug_name ?? '—'}</Box>
              <Box role="cell" sx={TD}>{formatUsDateTime(r.created_at)}</Box>
              <Box role="cell" sx={TD}>
                {r.waiting_for_previous_signer
                  ? <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>Waiting for an earlier signer</Box>
                  : <AppButton variant="contained" size="small" onClick={() => setSigning(r.envelope_id)}>Review &amp; sign</AppButton>}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Pager page={page} totalPages={q.data.total_pages} total={q.data.total} pageSize={PAGE_SIZE} onPage={setPage} showPageSize={false} label="documents" />
      {signing && <InAppSignDialog envelopeId={signing} onClose={() => setSigning(null)} onSigned={() => { setSigning(null); void q.refetch() }} />}
    </Section>
  )
}
